import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface DateRange {
  start: Date;
  end: Date;
  type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DateRangePickerService {
  // BehaviorSubject untuk mengelola state date range
  private dateRangeSubject = new BehaviorSubject<DateRange>({
    start: new Date(),
    end: new Date(),
    type: 'mtd'
  });

  // Observable yang dapat di-subscribe oleh komponen
  public dateRange$ = this.dateRangeSubject.asObservable();
  private static instance: DateRangePickerService;

  constructor() {
    // Singleton pattern untuk memastikan hanya ada satu instance service
    if (DateRangePickerService.instance) {
      return DateRangePickerService.instance;
    }

    DateRangePickerService.instance = this;

    // Berlangganan event storage untuk mendeteksi perubahan dari tab/jendela lain
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    // Inisialisasi dengan nilai dari localStorage atau default ke mtd
    this.initializeFromLocalStorage();
  }

  public hasStoredDateRange(): boolean {
    return localStorage.getItem('startDate') !== null &&
           localStorage.getItem('endDate') !== null &&
           localStorage.getItem('rangeType') !== null;
  }

  public initializeFromLocalStorage(): void {
    const startDateStr = localStorage.getItem('startDate');
    const endDateStr = localStorage.getItem('endDate');
    const rangeType = localStorage.getItem('rangeType') || 'mtd';

    if (startDateStr && endDateStr) {
      // Gunakan nilai yang ada
      this.dateRangeSubject.next({
        start: new Date(startDateStr),
        end: new Date(endDateStr),
        type: rangeType
      });
    } else {
      // PENTING: Jika tidak ada data di localStorage, buat dan simpan 'mtd'
      const range = this.getDateRange('mtd');
      this.saveDateRange(range.start, range.end, 'mtd');
    }
  }


  private handleStorageChange(event: StorageEvent): void {
    // Jika perubahan terjadi pada key yang kita gunakan
    if (event.key === 'startDate' || event.key === 'endDate' || event.key === 'rangeType') {
      this.initializeFromLocalStorage();
    }
  }

  // Metode untuk menyimpan date range dan memperbarui BehaviorSubject
  saveDateRange(start: Date, end: Date, type: string = 'custom'): void {
    // Format tanggal untuk localStorage
    const startFormatted = this.formatDateIso(start);
    const endFormatted = this.formatDateIso(end);

    // Simpan ke localStorage
    localStorage.setItem('startDate', startFormatted);
    localStorage.setItem('endDate', endFormatted);
    localStorage.setItem('rangeType', type);

    // Perbarui BehaviorSubject
    this.dateRangeSubject.next({ start, end, type });
  }

  // Metode untuk mengambil predefined date range
  getDateRange(type: string): DateRange {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    let start = new Date();
    let end = new Date(today);
    end.setDate(end.getDate() - 1);

    switch (type) {
      case 'all':
        // Set to a very early date and today
        start = new Date(2000, 0, 1);
        break;

      case 'last7':
        // Last 7 days
        start = new Date(today);
        start.setDate(today.getDate() - 6); // Kurangi 6 hari untuk mendapatkan 7 hari termasuk hari ini
        break;

      case 'last30':
        // Last 30 days
        start = new Date(today);
        start.setDate(today.getDate() - 29); // Kurangi 29 hari untuk mendapatkan 30 hari termasuk hari ini
        break;

      case 'lastYear':
        start = new Date(today);
        start.setFullYear(today.getFullYear() - 1);
        start.setMonth(0); // Januari = 0
        start.setDate(1);

        end = new Date(today);
        end.setFullYear(today.getFullYear() - 1);
        end.setMonth(11); // Desember = 11
        end.setDate(31);
        break;

      case 'lastMonth':
        // Last month
        start = new Date(today);
        start.setMonth(today.getMonth() - 1);
        start.setDate(1); // Mulai dari tanggal 1 bulan lalu
        end = new Date(today);
        end.setDate(0); // Set ke tanggal terakhir bulan lalu
        break;

      case 'lastWeek':
        // Last week (Minggu sampai Sabtu)
        const dayOfWeek = today.getDay(); // 0 (Minggu) - 6 (Sabtu)
        const daysSinceMonday = (dayOfWeek + 7) % 7; // Hitung hari sejak minggu
        start = new Date(today);
        start.setDate(today.getDate() - daysSinceMonday - 7); // Mulai dari Senin minggu lalu
        end = new Date(today);
        end.setDate(today.getDate() - daysSinceMonday - 1); // Berakhir pada Minggu minggu lalu
        break;
        
      case 'mtd':
        // Month to date
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      case 'ytd':
        // Year to date
        start = new Date(today.getFullYear(), 0, 1);
        break;

      case 'custom':
        // Default to mtd for custom until user selects
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      default:
        // Default to mtd
        start =  new Date(today.getFullYear(), today.getMonth(), 1);;
    }

    // Reset hours to 00:00:00 for start
    start.setHours(0, 0, 0, 0);

    return { start, end, type };
  }

 
  // Metode untuk menghasilkan kalendar bulan
  generateCalendarMonth(date: Date): any[][] {
    const month = date.getMonth();
    const year = date.getFullYear();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const calendar: any[][] = [];
    let week: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      week.push(null);
    }

    // Fill in the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      week.push(currentDate);

      if (week.length === 7) {
        calendar.push(week);
        week = [];
      }
    }

    // Add empty cells for remaining days after the last day of the month
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      calendar.push(week);
    }

    return calendar;
  }

  // Utilitas untuk memeriksa apakah dua tanggal adalah hari yang sama
  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  }

  // Format tanggal ke format YYYY-MM-DD untuk localStorage
  private formatDateIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
